import { Bell, BellOff } from 'lucide-react';
import { useToast } from '../components/ui';
import { useAlertWatch } from './useAlertWatch';

/**
 * Inline "get an audible + popup alert on this" toggle — the contextual
 * counterpart to the full Settings > Alert sounds picker. `size="sm"` is for
 * dropping it into a table row (e.g. a resource list) alongside other
 * per-row icon actions; the default is for a page header's button row.
 */
export function WatchToggle({
  scope,
  id,
  name,
  size,
}: {
  scope: 'site' | 'resource';
  id: string;
  name: string;
  size?: 'sm';
}) {
  const toast = useToast();
  const { loading, busy, isWatchingSite, isWatchingResource, toggleSite, toggleResource } = useAlertWatch();

  if (loading) return null;

  const watching = scope === 'site' ? isWatchingSite(id) : isWatchingResource(id);
  const toggle = scope === 'site' ? toggleSite : toggleResource;

  return (
    <button
      className={size === 'sm' ? 'btn btn-ghost btn-icon btn-sm' : 'btn btn-icon'}
      disabled={busy}
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await toggle(id);
          toast(watching ? `Stopped watching ${name}` : `Watching ${name} — you'll be alerted on state changes`, 'good');
        } catch (err) {
          toast('Could not save', 'bad', err instanceof Error ? err.message : undefined);
        }
      }}
      title={
        watching
          ? 'Stop watching — no more alerts for this'
          : 'Watch this — get notified when its state changes (enable browser notifications in Settings for popups outside the tab)'
      }
      aria-label={watching ? 'Stop watching' : 'Watch for state changes'}
    >
      {watching ? <Bell size={size === 'sm' ? 14 : 15} style={{ color: 'var(--itr-primary)' }} /> : <BellOff size={size === 'sm' ? 14 : 15} />}
    </button>
  );
}
